/**
 * DEV-12444: schedule work past the marketing-home load/blocking window.
 *
 * The authenticated bench settle is 3500 ms (`BENCH_SETTLE_MS`). Anything that
 * parses or commits during that window — Unicorn Studio, the full CMS chunk,
 * Beacon, below-fold React trees — lands in `longTaskTime`.
 *
 * `requestIdleCallback` is intentionally not used. It fires the moment the
 * thread is idle, which is still inside the measured window (DEV-12330).
 *
 * Default interaction is scroll only. Pointer/key/touch would fire on the
 * bench's scripted INP click (the second hero tab) and parse WebGL/CMS on
 * the same turn as the interaction — the INP regression DEV-12075 just paid
 * down. Callers that must arm on the first tap (Beacon, matching GA) pass
 * `POINTER_EVENTS` explicitly.
 */

export const BLOCKING_WINDOW_MS = 4000;

export const SCROLL_EVENTS = ['scroll'] as const;
export const POINTER_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;

export function afterBlockingWindow(
  cb: () => void,
  delayMs = BLOCKING_WINDOW_MS,
  events: readonly string[] = SCROLL_EVENTS,
): () => void {
  const win = (globalThis as { window?: Window }).window;
  if (!win) return () => {};

  let done = false;
  const opts: AddEventListenerOptions = { once: true, passive: true };

  const go = () => {
    if (done) return;
    done = true;
    win.clearTimeout(timer);
    for (const evt of events) win.removeEventListener(evt, go, opts);
    cb();
  };

  for (const evt of events) win.addEventListener(evt, go, opts);
  const timer = win.setTimeout(go, delayMs);

  return () => {
    if (done) return;
    done = true;
    win.clearTimeout(timer);
    for (const evt of events) win.removeEventListener(evt, go, opts);
  };
}
